import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';

const toOption = (option) => {
  if (option && typeof option === 'object') {
    const label = option.label ?? option.name ?? option.value ?? '';
    const value = option.value ?? option.id ?? label;

    return {
      label,
      value,
    };
  }

  return {
    label: option,
    value: option,
  };
};

const getFilterSource = (source) => (
  Array.isArray(source) ? { values: source } : source || {}
);

const resolveRevenueMisFilterSource = (filter) => (
  Array.isArray(filter) ? { values: filter } : filter || {}
);

const buildProjectServiceApiUrl = (path) => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${path}`;
  }

  return `${baseUrl}:${servicePort}${serviceURL}/api${path}`;
};

const buildChoiceFilter = (field, label, source, template = 'choice') => {
  const { values = [], url } = getFilterSource(source);
  const options = values.map(toOption);

  return {
    type: 'filter',
    field,
    label,
    placeholder: `Select ${label}`,
    template,
    selectedValues: [],
    ...(url ? { url } : {}),
    options,
    values: options,
  };
};

const buildDateFilter = (field, label) => ({
  type: 'filter',
  field,
  label,
  placeholder: `Select ${label}`,
  template: 'dateRange',
  range: [null, null],
  selectedValues: [],
});

const createRevenueMisFilters = (filterOptions = {}) => {
  const filterBaseUrl = buildProjectServiceApiUrl('/v1/revenue-mis/filters');

  return [
    {
      label: 'Project Vertical',
      filter: buildChoiceFilter('revenueMisVerticalFilter', 'Project Vertical', {
        ...resolveRevenueMisFilterSource(filterOptions.vertical),
        url: `${filterBaseUrl}/project-verticals`,
      }, 'multiSelect'),
    },
    {
      label: 'Project Type',
      filter: buildChoiceFilter('revenueMisTypeFilter', 'Project Type', {
        ...resolveRevenueMisFilterSource(filterOptions.projectType),
        url: `${filterBaseUrl}/project-types`,
      }, 'multiSelect'),
    },
    {
      label: 'Project Status',
      filter: buildChoiceFilter('revenueMisStatusFilter', 'Project Status', {
        ...resolveRevenueMisFilterSource(filterOptions.status),
        url: `${filterBaseUrl}/project-status`,
      }, 'multiSelect'),
    },
    {
      label: 'Senior Manager',
      filter: buildChoiceFilter('revenueMisManagerFilter', 'Senior Manager', {
        ...resolveRevenueMisFilterSource(filterOptions.manager),
        url: `${filterBaseUrl}/managers`,
      }, 'multiSelect'),
    },
    {
      label: 'Project Name',
      filter: buildChoiceFilter('revenueMisProjectNameFilter', 'Project Name', {
        ...resolveRevenueMisFilterSource(filterOptions.projectName),
        url: `${filterBaseUrl}/project-names`,
      }, 'multiSelect'),
    },
    {
      label: 'Zoho Client Name',
      filter: buildChoiceFilter('revenueMisClientNameFilter', 'Zoho Client Name', {
        ...resolveRevenueMisFilterSource(filterOptions.clientName),
        url: `${filterBaseUrl}/clients`,
      }, 'multiSelect'),
    },
    {
      label: 'PO Number',
      filter: buildChoiceFilter('revenueMisPoNumberFilter', 'PO Number', {
        ...resolveRevenueMisFilterSource(filterOptions.poNumber),
        url: `${filterBaseUrl}/po-numbers`,
      }, 'multiSelect'),
    },
    {
      label: 'Milestone Status',
      filter: buildChoiceFilter('revenueMisMilestoneStatusFilter', 'Milestone Status', {
        ...resolveRevenueMisFilterSource(filterOptions.milestoneStatus),
        url: `${filterBaseUrl}/milestone-status`,
      }, 'multiSelect'),
    },
    {
      label: 'Invoice Status',
      filter: buildChoiceFilter('revenueMisInvoiceStatusFilter', 'Invoice Status', {
        ...resolveRevenueMisFilterSource(filterOptions.invoiceStatus),
        url: `${filterBaseUrl}/invoice-status`,
      }, 'multiSelect'),
    },
    {
      label: 'Revenue Date Range',
      filter: buildDateFilter('revenueMisRevenueDateRangeFilter', 'Revenue Date Range'),
    },
    {
      label: 'Revenue Type',
      filter: buildChoiceFilter(
        'revenueMisRevenueTypeFilter',
        'Revenue Type',
        {
          ...resolveRevenueMisFilterSource(filterOptions.revenueType),
          url: `${filterBaseUrl}/revenue-types`,
        },
        'multiSelect',
      ),
    },
  ];
};

const buildRevenueMisGlobalFilterColumns = (filterOptions) => (
  createRevenueMisFilters(filterOptions).map(({ label, filter }, index) => ({
    field: filter.field,
    header: label,
    width: 150,
    filter: true,
    visible: false,
    sortable: false,
    pinnable: false,
    order: index + 1,
    defaultPinned: false,
    isGlobalFilter: true,
    showFilterButtonInHeader: false,
    showPinButtonInHeader: false,
    menuItems: [
      {
        label,
        items: [filter],
      },
    ],
  }))
);

const baseRevenueMisColumns = [
  ['projectId', 'Project Id', 105],
  ['projectName', 'Project Name', 130],
  ['zohoCustomerName', 'Zoho Customer Name', 165],
  ['projectStatus', 'Project Status', 135],
  ['projectType', 'Project Type', 110],
  ['actualProjectStartDate', 'Actual Project Start date', 175],
  ['projectVertical', 'Project Vertical', 135],
  ['seniorManager', 'Sr.Manager', 120],
  ['country', 'Country', 110],
  ['region', 'Region', 110],
  ['totalRevenueInr', 'Total Revenue(INR)', 150, 'number'],
  ['totalRevenueUsd', 'Total Revenue(USD)', 150, 'number'],
  ['budgetedFxRate', 'Budgeted FX rate ( USD to INR)', 400, 'number'],
  ['zohoBillingEntity', 'Zoho Billing /Accrued Entity', 300],
];

const detailedRevenueMisColumns = [
  ['rowSelector', '', 44],
  ['accruedInvoicedDate', 'Accrued/Invoiced date', 210],
  ['projectId', 'Project Id', 105],
  ['projectName', 'Project Name', 130],
  ['milestoneZohoCustomerName', 'Project Customer Name', 240],
  ['projectType', 'Project Type', 110],
  ['actualProjectStartDate', 'Actual Project Start date', 175],
  ['projectStatus', 'Project Status', 135],
  ['projectVertical', 'Project Vertical', 135],
  ['seniorManager', 'Sr.Manager', 120],
  ['zohoBillingEntity', 'Zoho Billing /Accrued Entity', 300],
  ['country', 'Country', 110],
  ['region', 'Region', 110],
  ['milestoneId', 'Milestone ID', 135],
  ['milestoneCategory', 'Milestone/Category', 170],
  ['milestoneStatus', 'Milestone Status', 150],
  ['invoiceStatus', 'Invoice Status', 140],
  ['revenueType', 'Revenue Type', 130],
  ['totalRevenueInr', 'Total Revenue(INR)', 150, 'number'],
  ['totalRevenueUsd', 'Total Revenue(USD)', 150, 'number'],
  ['budgetedFxRate', 'Budgeted FX rate ( USD to INR)', 400, 'number'],
  ['zohoInvoiceOrJvNumber', 'Zoho Invoice No/ JV Number', 240],
  ['invoicedAccruedZohoClientName', 'Invoiced/Accrued Zoho Client Name', 320],
  ['revenueRecognitionMonth', 'Revenue Recognition Month', 190],
  ['remarks', 'Remarks ( If Any)', 220],
  ['rowActions', 'Action', 96],
];

const applyRevenueMisColumnType = (column, type) => {
  const columnWithOverflow = ['zohoCustomerName', 'milestoneZohoCustomerName'].includes(column.field)
    ? { ...column, ellipsis: true }
    : column;

  if (type !== 'number') return columnWithOverflow;

  return {
    ...columnWithOverflow,
    type,
    numeric: true,
    align: 'right',
    headerAlign: 'right',
    style: { ...(column.style || {}), textAlign: 'right' },
    headerStyle: { ...(column.headerStyle || {}), textAlign: 'right' },
  };
};

const buildRevenueMisDataColumns = (globalFilterCount, activeMonths = []) => {
  const baseCols = baseRevenueMisColumns.map(
    ([field, header, width, type], index) => applyRevenueMisColumnType({
      field,
      header,
      width,
      filter: false,
      visible: true,
      sortable: false,
      pinnable: false,
      order: globalFilterCount + index + 1,
      defaultPinned: field === 'projectId' || field === 'projectName',
      isGlobalFilter: false,
      showFilterButtonInHeader: false,
      showPinButtonInHeader: false,
      menuItems: [],
    }, type),
  );

  let currentOrder = globalFilterCount + baseCols.length + 1;
  const monthCols = [];
  const targetMonths = Array.isArray(activeMonths) && activeMonths.length > 0
    ? activeMonths
    : [];

  targetMonths.forEach((monthKey) => {
    const prefix = monthKey.replace(/['\s]/g, '');
    const inrField = `${prefix}_totalRevenueInr`;
    const usdField = `${prefix}_totalRevenueUsd`;

    monthCols.push(applyRevenueMisColumnType({
      field: inrField,
      header: monthKey,
      subHeader: 'Total Revenue(INR)',
      width: 160,
      filter: false,
      visible: true,
      sortable: false,
      pinnable: false,
      order: currentOrder++,
      defaultPinned: false,
      isGlobalFilter: false,
      showFilterButtonInHeader: false,
      showPinButtonInHeader: false,
      menuItems: [],
    }, 'number'));

    monthCols.push(applyRevenueMisColumnType({
      field: usdField,
      header: monthKey,
      subHeader: 'Total Revenue(USD)',
      width: 160,
      filter: false,
      visible: true,
      sortable: false,
      pinnable: false,
      order: currentOrder++,
      defaultPinned: false,
      isGlobalFilter: false,
      showFilterButtonInHeader: false,
      showPinButtonInHeader: false,
      menuItems: [],
    }, 'number'));
  });

  return [...baseCols, ...monthCols];
};

const buildDetailedRevenueMisDataColumns = (globalFilterCount = 0) => detailedRevenueMisColumns.map(
  ([field, header, width, type], index) => applyRevenueMisColumnType({
    field,
    header,
    width,
    filter: false,
    visible: true,
    sortable: false,
    pinnable: false,
    order: globalFilterCount + index + 1,
    defaultPinned: field === 'rowSelector' || field === 'projectId' || field === 'projectName',
    isGlobalFilter: false,
    showFilterButtonInHeader: false,
    showPinButtonInHeader: false,
    menuItems: [],
  }, type),
);

export const buildRevenueMisTableConfig = (filterOptions = {}, activeMonths = []) => {
  const globalFilterColumns = buildRevenueMisGlobalFilterColumns(filterOptions);
  const dataColumns = buildRevenueMisDataColumns(globalFilterColumns.length, activeMonths);

  return [
    ...globalFilterColumns,
    ...dataColumns,
  ];
};

export const buildDetailedRevenueMisTableConfig = (options = {}) => {
  const { canUpdate = true, canDelete = true } = options;

  return buildDetailedRevenueMisDataColumns().filter((column) => {
    if (column.field === 'rowSelector') return canDelete;
    if (column.field === 'rowActions') return canUpdate || canDelete;
    return true;
  });
};
